import { Controller } from "@hotwired/stimulus";
import { getCredential } from "../lib/webauthn";

// Offers passkey sign-in two ways: conditional UI (autofill) started on connect,
// and an explicit button. The button aborts any in-flight conditional request
// first — two concurrent navigator.credentials.get calls throw NotAllowedError.
export default class extends Controller {
  static values = { optionsUrl: String, callbackUrl: String };

  connect() {
    this.conditionalController = null;
    this.startConditional();
  }

  disconnect() {
    this.conditionalController?.abort();
  }

  async startConditional() {
    if (
      !window.PublicKeyCredential ||
      !PublicKeyCredential.isConditionalMediationAvailable
    ) {
      return;
    }
    if (!(await PublicKeyCredential.isConditionalMediationAvailable())) {
      return;
    }

    this.conditionalController = new AbortController();
    try {
      const options = await this.fetchOptions();
      if (!options) return;
      const credential = await getCredential(options, {
        mediation: "conditional",
        signal: this.conditionalController.signal,
      });
      await this.submit(credential);
    } catch (error) {
      // Aborted (button took over) or no passkey chosen — nothing to do.
    }
  }

  async authenticate() {
    if (!window.PublicKeyCredential) {
      return;
    }

    // Abort the conditional request so the modal get() doesn't collide, and
    // re-fetch options so we verify against a fresh single-use challenge.
    this.conditionalController?.abort();

    try {
      const options = await this.fetchOptions();
      if (!options) return;
      const credential = await getCredential(options, {
        mediation: "optional",
      });
      await this.submit(credential);
    } catch (error) {
      // User dismissed the prompt — leave the form as-is.
    }
  }

  async fetchOptions() {
    const response = await fetch(this.optionsUrlValue, {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return null;
    return response.json();
  }

  async submit(credential) {
    const response = await fetch(this.callbackUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken,
      },
      body: JSON.stringify({ credential }),
    });

    if (response.ok) {
      const data = await response.json();
      window.location = data.redirect_to;
    }
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content;
  }
}
